import React from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSubmit,
  DropdownMenuInput,
  DropdownSearchInput,
  DropdownMenuHeader,
  DropdownMenuTitle,
} from "~/react/ui/dropdown-menu"
import { Button } from "~/react/ui/button"
import { Checkbox } from "~/react/ui/checkbox"
import { AsyncSelect } from "~/react/ui/select"
import { Tag } from "~/react/ui/tag"
import { Radio } from "~/react/ui/radio"
import { Options } from "react-select"
import { locations_path } from "~/lib/routes"
import {
  Department as SharedDepartment,
  VerificationStatus as SharedVerificationStatus,
  departmentsList as sharedDepartmentsList,
  verificationStatusList as sharedVerificationStatusList,
} from "~/react/components/filters/shared"
import { ResultType } from "~/react/components/multi_domain_search/result"
import useIsInView from "@/react/hooks/use-is-in-view"

const clearFilters = (setFilters: (filters: FiltersType) => void) => {
  setFilters({
    requiredFields: [],
    departments: [],
    jobTitles: [],
    verificationStatus: [],
    hideAlreadySaved: false,
    peoplePerCompany: undefined,
    location: [],
  })
}

const sortFunction = (a: ResultType, b: ResultType) => {
  const getPriorityScore = (result: ResultType) => {
    if (result.decisionMaker && result.fullNameExists) {
      return 1
    }

    if (result.decisionMaker) {
      return 2
    }

    if (result.fullNameExists && result.jobTitle) {
      return 3
    }

    if (result.fullNameExists && !result.jobTitle) {
      return 4
    }

    if (!result.fullNameExists && result.jobTitle) {
      return 5
    }

    return 6
  }

  const scoreA = getPriorityScore(a)
  const scoreB = getPriorityScore(b)

  return scoreA - scoreB
}

const filterFunction = (
  results: ResultType[],
  filters: FiltersType,
  locationsMappings: LocationsMappingsType,
  revealedIds: number[],
  ignoreFilters: VisibleFilters[] = [],
) => {
  const includeLocation = filters.location.filter(
    (location) => location.included,
  )
  const excludeLocation = filters.location.filter(
    (location) => !location.included,
  )
  // Apply all standard filters first
  let baseFilteredResults = results
    .filter((result) => {
      if (filters.location.length === 0 || ignoreFilters.includes("location")) {
        // Always match if no location filters are set or if location is being ignored
        return true
      }

      const matchesLocation = (result: ResultType, location: Location) => {
        if (location.type === "continent") {
          return locationsMappings.continents[location.value].includes(
            result.country,
          )
        }
        if (location.type === "business_region") {
          return locationsMappings.business_regions[location.value].includes(
            result.country,
          )
        }
        if (location.type === "country") {
          return result.country === location.value
        }
        if (location.type === "state") {
          return result.state === location.value
        }
        if (location.type === "city") {
          const [city, state, country] = location.value.split(",")

          return (
            result.city === city &&
            (state === "" || result.state === state) &&
            result.country === country
          )
        }
      }
      const matchesAnyExcludeLocation = excludeLocation.some((location) =>
        matchesLocation(result, location),
      )
      const matchesAnyIncludeLocation = includeLocation.some((location) =>
        matchesLocation(result, location),
      )
      if (matchesAnyExcludeLocation) {
        // Exclude if any exclude location matches
        return false
      }
      if (includeLocation.length > 0) {
        // Include if any include location matches
        return matchesAnyIncludeLocation
      }

      return true
    })
    .filter((result) => {
      if (ignoreFilters.includes("required_field")) {
        return true
      }
      if (filters.requiredFields.length > 0) {
        let requiredFieldsMet: boolean = true
        if (filters.requiredFields.includes("full_name")) {
          requiredFieldsMet = requiredFieldsMet && result.fullNameExists
        }
        if (filters.requiredFields.includes("phone_number")) {
          requiredFieldsMet = requiredFieldsMet && result.phoneNumberExists
        }
        if (filters.requiredFields.includes("position")) {
          requiredFieldsMet = requiredFieldsMet && result.jobTitle !== null
        }
        return requiredFieldsMet
      }
      return true
    })
    .filter((result) => {
      if (ignoreFilters.includes("department")) {
        return true
      }
      if (filters.departments.length > 0) {
        if (result.department === null) {
          return false
        }
        return filters.departments.some((department) => {
          const normalizedDepartment =
            department === "finance" ? "finance & accounting" : department
          return result.department
            ?.toLowerCase()
            .includes(normalizedDepartment.toLowerCase())
        })
      }
      return true
    })
    .filter((result) => {
      if (ignoreFilters.includes("job_title")) {
        return true
      }
      if (filters.jobTitles.length > 0) {
        return filters.jobTitles.some((jobTitle) =>
          result.jobTitle?.toLowerCase().includes(jobTitle.toLowerCase()),
        )
      }
      return true
    })
    .filter((result) => {
      if (ignoreFilters.includes("already_saved")) {
        return true
      }
      if (filters.hideAlreadySaved) {
        return result.lead === null || revealedIds.includes(result.id)
      }
      return true
    })
    .filter((result) => {
      if (ignoreFilters.includes("verification_status")) {
        return true
      }
      if (filters.verificationStatus.length > 0) {
        if (
          !filters.verificationStatus.includes("unknown") &&
          (result.verification === null || result.verification === undefined)
        ) {
          return false
        } else if (
          filters.verificationStatus.includes("unknown") &&
          (result.verification.status === "unknown" ||
            result.verification.status === null ||
            result.verification.status === undefined)
        ) {
          return true
        } else {
          return filters.verificationStatus.includes(
            result.verification.status as any,
          )
        }
      }
      return true
    })

  // Apply people per company filter if set
  if (
    filters.peoplePerCompany &&
    filters.peoplePerCompany.type !== "all" &&
    !ignoreFilters.includes("people_per_company")
  ) {
    // Group results by domain
    const resultsByDomain = baseFilteredResults.reduce(
      (acc, result) => {
        const domain = result.domain || "unknown"
        if (!acc[domain]) {
          acc[domain] = []
        }
        acc[domain].push(result)
        return acc
      },
      {} as Record<string, ResultType[]>,
    )

    // Apply per-company filtering
    const limitedResults: ResultType[] = []

    Object.values(resultsByDomain).forEach((domainResults) => {
      let filteredDomainResults = domainResults

      if (filters.peoplePerCompany!.type === "decision_makers") {
        // Only show decision makers
        filteredDomainResults = domainResults.filter(
          (result) => result.decisionMaker,
        )
      } else if (filters.peoplePerCompany!.type === "specific_number") {
        // Limit to specific number per company
        const limit = filters.peoplePerCompany!.specificNumber
        // Sort by decision makers first, then by full name availability
        filteredDomainResults = domainResults.sort(sortFunction).slice(0, limit)
      }

      limitedResults.push(...filteredDomainResults)
    })

    baseFilteredResults = limitedResults
  }

  return baseFilteredResults
}

// Use shared department list
const departmentsList = sharedDepartmentsList
type Type = "personal" | "generic"
const typesList: Record<Type, string> = {
  personal: "Personal",
  generic: "Generic",
}
const requiredFieldsList: Record<RequiredField, string> = {
  phone_number: "Phone number",
  position: "Job title",
  full_name: "Full name",
}
// Use shared verification status list
const verificationStatusList = sharedVerificationStatusList

// Use shared Department type
type Department = SharedDepartment
type RequiredField = "phone_number" | "position" | "full_name"
// Use shared VerificationStatus type
type VerificationStatus = SharedVerificationStatus
type PeoplePerCompany = {
  type: "all" | "decision_makers" | "specific_number"
  specificNumber?: number
}
type Location = {
  value: string
  type: "continent" | "business_region" | "country" | "state" | "city"
  label: string
  included: boolean
}
type SerializedLocation = {
  value: string
  label: string
  type: "continent" | "business_region" | "country" | "state" | "city"
  country: string | null
  city: string | null
  state: string | null
}

type LocationsMappingsType = {
  continents: Record<string, string[]>
  business_regions: Record<string, string[]>
}
type FiltersType = {
  departments: Department[]
  jobTitles: string[]
  requiredFields: RequiredField[]
  hideAlreadySaved: boolean
  verificationStatus: VerificationStatus[]
  peoplePerCompany?: PeoplePerCompany
  location: Location[]
  similarityRange?: [number, number]
}
type VisibleFilters =
  | "department"
  | "job_title"
  | "already_saved"
  | "verification_status"
  | "required_field"
  | "people_per_company"
  | "location"
  | "similarity_score"
const allVisibleFilters: VisibleFilters[] = [
  "department",
  "job_title",
  "already_saved",
  "verification_status",
  "required_field",
  "people_per_company",
  "location",
  "similarity_score",
]
const allFilters: Record<VisibleFilters, string> = {
  department: "js.multi_domain_search.filters.department",
  job_title: "js.multi_domain_search.filters.job_title",
  already_saved: "js.multi_domain_search.filters.already_saved.title",
  verification_status:
    "js.multi_domain_search.filters.verification_status.title",
  required_field: "js.multi_domain_search.filters.required_field",
  people_per_company: "js.multi_domain_search.filters.people_per_company.title",
  location: "js.multi_domain_search.filters.location.title",
  similarity_score: "js.lead_lookalikes.filters.similarity_score",
}
interface FiltersProps {
  possibleFilters: VisibleFilters[]
  filters: FiltersType | null
  setFilters: (filters: FiltersType) => void
  jobTitles: string[]
  clearFilters: () => void
  anyFiltersSet: boolean
  additionalFilter?: React.ReactNode
}
const Filters = ({
  possibleFilters = allVisibleFilters,
  filters,
  setFilters,
  jobTitles,
  clearFilters,
  anyFiltersSet,
  additionalFilter,
  ...props
}: React.ComponentProps<typeof DropdownMenu> & FiltersProps) => {
  const [selectedDepartments, setSelectedDepartments] = React.useState<
    Department[]
  >(filters?.departments || [])
  const [selectedJobTitles, setSelectedJobTitles] = React.useState<string[]>(
    filters?.jobTitles || [],
  )
  const [customJobTitles, setCustomJobTitles] = React.useState<string[]>([])
  const [jobTitleFilterInput, setJobTitleFilterInput] =
    React.useState<string>("")
  const [selectedRequiredFields, setSelectedRequiredFields] = React.useState<
    RequiredField[]
  >(filters?.requiredFields || [])
  const [hideAlreadySaved, setHideAlreadySaved] = React.useState<boolean>(
    filters?.hideAlreadySaved || true,
  )
  const [selectedVerificationStatus, setSelectedVerificationStatus] =
    React.useState<VerificationStatus[]>(filters?.verificationStatus || [])
  const [selectedPeoplePerCompany, setSelectedPeoplePerCompany] =
    React.useState<PeoplePerCompany>({
      type: "all",
      ...(filters?.peoplePerCompany || {}),
    })
  const [selectedLocation, setSelectedLocation] = React.useState<
    Location[] | null
  >(filters?.location || [])
  const [selectedSimilarityRange, setSelectedSimilarityRange] = React.useState<
    [number, number]
  >(filters?.similarityRange || [40, 100])
  const [visibleFilters, setVisibleFilters] = React.useState<VisibleFilters[]>(
    ["job_title", "verification_status", "already_saved"].filter((f) =>
      possibleFilters.includes(f as VisibleFilters),
    ),
  )
  const [currentFilter, setCurrentFilter] =
    React.useState<VisibleFilters | null>(null)

  const [jobTitleVirtualizerEnabled, setJobTitleVirtualizerEnabled] =
    React.useState(false)

  // Refs for dropdown triggers
  const alreadySavedTriggerRef = React.useRef<HTMLButtonElement>(null)
  const verificationStatusTriggerRef = React.useRef<HTMLButtonElement>(null)
  const departmentTriggerRef = React.useRef<HTMLButtonElement>(null)
  const jobTitleTriggerRef = React.useRef<HTMLButtonElement>(null)
  const requiredFieldTriggerRef = React.useRef<HTMLButtonElement>(null)
  const locationTriggerRef = React.useRef<HTMLButtonElement>(null)
  const peoplePerCompanyTriggerRef = React.useRef<HTMLButtonElement>(null)
  const similarityScoreTriggerRef = React.useRef<HTMLButtonElement>(null)
  const addFilterTriggerRef = React.useRef<HTMLButtonElement>(null)
  const jobTitleListRef = React.useRef<HTMLDivElement>(null)

  // Helper function to highlight matching text in job titles
  const highlightMatch = (title: string, query: string): React.ReactNode => {
    if (!query || query.length === 0) {
      return title
    }

    const lowerTitle = title.toLowerCase()
    const lowerQuery = query.toLowerCase()
    const matchIndex = lowerTitle.indexOf(lowerQuery)

    if (matchIndex === -1) {
      return title
    }

    const beforeMatch = title.slice(0, matchIndex)
    const match = title.slice(matchIndex, matchIndex + query.length)
    const afterMatch = title.slice(matchIndex + query.length)

    return (
      <>
        {beforeMatch}
        <mark className="dropdown-search__match">{match}</mark>
        {afterMatch}
      </>
    )
  }

  // Filter job titles based on search input
  const filteredJobTitles = React.useMemo(() => {
    // Combine job titles and custom titles, removing duplicates
    const allTitles = [...jobTitles, ...customJobTitles]
    if (jobTitleFilterInput.length >= 1) {
      return allTitles.filter((title) =>
        title.toLowerCase().includes(jobTitleFilterInput.toLowerCase()),
      )
    }
    return allTitles
  }, [jobTitles, customJobTitles, jobTitleFilterInput])
  const uniqueTitles = [
    ...new Set([...selectedJobTitles, ...filteredJobTitles]),
  ]

  const isIntersecting = useIsInView(jobTitleListRef)

  React.useEffect(() => {
    setJobTitleVirtualizerEnabled(() =>
      jobTitleListRef.current?.checkVisibility(),
    )
  }, [jobTitleListRef, isIntersecting])

  // Virtualizer for job titles list
  const jobTitleVirtualizer = useVirtualizer({
    count: uniqueTitles.length,
    getScrollElement: () => jobTitleListRef.current,
    estimateSize: React.useCallback(() => 40, []),
    overscan: 10,
    enabled: jobTitleVirtualizerEnabled,
  })

  React.useEffect(() => {
    setSelectedDepartments(filters?.departments || [])
    setSelectedJobTitles(filters?.jobTitles || [])
    setSelectedRequiredFields(filters?.requiredFields || [])
    setHideAlreadySaved(filters?.hideAlreadySaved || false)
    setSelectedVerificationStatus(filters?.verificationStatus || [])
    setSelectedPeoplePerCompany({
      type: "all",
      ...(filters?.peoplePerCompany || {}),
    })
    setSelectedLocation(filters?.location || [])
    setSelectedSimilarityRange(filters?.similarityRange || [40, 100])
    // Initialize custom job titles from filters (titles not in the jobTitles prop)
    if (filters?.jobTitles) {
      const customTitles = filters.jobTitles.filter(
        (title) => !jobTitles.includes(title),
      )
      setCustomJobTitles(customTitles)
    }
  }, [filters, jobTitles])

  const closeDropdown = (
    triggerRef: React.RefObject<HTMLButtonElement | null>,
  ) => {
    if (triggerRef.current && window.bootstrap) {
      const dropdownInstance = window.bootstrap.Dropdown.getInstance(
        triggerRef.current,
      )
      dropdownInstance?.hide()
    }
  }

  // Helper functions for job title filter
  const handleSelectAllJobTitles = () => {
    const uniqueTitles = new Set([...selectedJobTitles, ...filteredJobTitles])
    setSelectedJobTitles(Array.from(uniqueTitles))
  }

  const handleResetJobTitles = () => {
    setSelectedJobTitles([])
  }

  const handleCreateCustomJobTitle = () => {
    if (
      jobTitleFilterInput &&
      !selectedJobTitles.includes(jobTitleFilterInput)
    ) {
      const customTitle = jobTitleFilterInput
      const updatedJobTitles = [customTitle, ...selectedJobTitles]
      setSelectedJobTitles(updatedJobTitles)
      // Add to custom titles list if it's not in the original jobTitles prop
      if (!jobTitles.includes(customTitle)) {
        setCustomJobTitles((prev) => {
          if (!prev.includes(customTitle)) {
            return [customTitle, ...prev]
          }
          return prev
        })
      }

      // Apply filters immediately with the updated job titles
      applyFilters({ jobTitles: updatedJobTitles })
    }
  }

  const applyFilters = (overrides?: Partial<FiltersType>) => {
    setFilters({
      departments: selectedDepartments,
      jobTitles: selectedJobTitles,
      requiredFields: selectedRequiredFields,
      hideAlreadySaved: hideAlreadySaved,
      verificationStatus: selectedVerificationStatus,
      peoplePerCompany:
        selectedPeoplePerCompany.type !== "all"
          ? selectedPeoplePerCompany
          : undefined,
      location: selectedLocation,
      similarityRange:
        selectedSimilarityRange[0] !== 40 || selectedSimilarityRange[1] !== 100
          ? selectedSimilarityRange
          : undefined,
      ...overrides,
    })
    setVisibleFilters((prev) => [...prev, currentFilter as VisibleFilters])
    setCurrentFilter(null)
    setJobTitleFilterInput("")

    // Close the appropriate dropdown
    if (currentFilter) {
      // We're in the nested "Add filter" dropdown
      closeDropdown(addFilterTriggerRef)
    } else {
      // We're in a direct filter dropdown - determine which one based on visible filters
      // The dropdown that was just interacted with should be closed
      // We'll close all of them to be safe, as only one should be open at a time
      closeDropdown(alreadySavedTriggerRef)
      closeDropdown(verificationStatusTriggerRef)
      closeDropdown(departmentTriggerRef)
      closeDropdown(jobTitleTriggerRef)
      closeDropdown(requiredFieldTriggerRef)
      closeDropdown(locationTriggerRef)
      closeDropdown(peoplePerCompanyTriggerRef)
      closeDropdown(similarityScoreTriggerRef)
    }
  }

  const nonVisibleFilters: VisibleFilters[] = Object.keys(allFilters).filter(
    (key) =>
      !visibleFilters.includes(key as VisibleFilters) &&
      possibleFilters.includes(key as VisibleFilters),
  ) as VisibleFilters[]

  // Filters constructs
  const alreadySavedFilterBody = (
    <>
      <DropdownMenuItem key="already_saved_hide">
        <Checkbox
          checked={hideAlreadySaved}
          id="filter-already-saved"
          name="filter-already-saved"
          onChange={() => setHideAlreadySaved((prev) => !prev)}
        />
        <label htmlFor="filter-already-saved">
          {i18n.t("js.multi_domain_search.filters.already_saved.hide")}
        </label>
      </DropdownMenuItem>
      <DropdownMenuSubmit onClick={() => applyFilters()}>
        {i18n.t("js.multi_domain_search.filters.apply_filters")}
      </DropdownMenuSubmit>
    </>
  )
  const alreadySavedFilter = (
    <div className="h-filter" key="already_saved">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            ref={alreadySavedTriggerRef}
          >
            {i18n.t("js.multi_domain_search.filters.already_saved.title")}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>{alreadySavedFilterBody}</DropdownMenuContent>
      </DropdownMenu>
      {filters?.hideAlreadySaved && (
        <div className="h-filter__value">
          {i18n.t(
            `js.multi_domain_search.filters.already_saved.${filters?.hideAlreadySaved}`,
          )}
          <button
            className="btn-empty h-filter__remove"
            onClick={() => applyFilters({ hideAlreadySaved: false })}
          >
            <span className="far fa-times" aria-hidden="true"></span>
          </button>
        </div>
      )}
    </div>
  )
  const verificationStatusFilterBody = (
    <>
      {Object.entries(verificationStatusList).map(([key, value]) => (
        <DropdownMenuItem key={key}>
          <Checkbox
            checked={selectedVerificationStatus.includes(
              key as VerificationStatus,
            )}
            id={`filter-verification-status-${key}`}
            name="filter-verification-status"
            onChange={() =>
              setSelectedVerificationStatus((prev) =>
                prev.includes(key as VerificationStatus)
                  ? prev.filter((item) => item !== (key as VerificationStatus))
                  : [...prev, key as VerificationStatus],
              )
            }
          />
          <label htmlFor={`filter-verification-status-${key}`}>{value}</label>
        </DropdownMenuItem>
      ))}
      <DropdownMenuSubmit onClick={() => applyFilters()}>
        {i18n.t("js.multi_domain_search.filters.apply_filters")}
      </DropdownMenuSubmit>
    </>
  )
  const verificationStatusFilter = (
    <div className="h-filter" key="verification_status">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            ref={verificationStatusTriggerRef}
          >
            {i18n.t("js.multi_domain_search.filters.verification_status.title")}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {verificationStatusFilterBody}
        </DropdownMenuContent>
      </DropdownMenu>
      {filters?.verificationStatus?.map((status) => (
        <div className="h-filter__value" key={status}>
          {verificationStatusList[status]}
          <button
            className="btn-empty h-filter__remove"
            onClick={() =>
              applyFilters({
                verificationStatus:
                  filters?.verificationStatus?.filter(
                    (item) => item !== status,
                  ) || [],
              })
            }
          >
            <span className="far fa-times" aria-hidden="true"></span>
          </button>
        </div>
      ))}
    </div>
  )
  const departmentFilterBody = (
    <>
      {Object.entries(departmentsList).map(([key, value]) => (
        <DropdownMenuItem key={key}>
          <Checkbox
            checked={selectedDepartments.includes(key as Department)}
            id={`filter-department-${key}`}
            name="filter-department"
            onChange={() =>
              setSelectedDepartments((prev) =>
                prev.includes(key as Department)
                  ? prev.filter((item) => item !== (key as Department))
                  : [...prev, key as Department],
              )
            }
          />
          <label htmlFor={`filter-department-${key}`}>{value}</label>
        </DropdownMenuItem>
      ))}
      <DropdownMenuSubmit onClick={() => applyFilters()}>
        {i18n.t("js.multi_domain_search.filters.apply_filters")}
      </DropdownMenuSubmit>
    </>
  )
  const departmentFilter = (
    <div className="h-filter" key="department">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            ref={departmentTriggerRef}
          >
            {i18n.t("js.multi_domain_search.filters.department")}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>{departmentFilterBody}</DropdownMenuContent>
      </DropdownMenu>
      {filters?.departments?.map((department) => (
        <div className="h-filter__value" key={department}>
          {departmentsList[department]}
          <button
            className="btn-empty h-filter__remove"
            onClick={() =>
              applyFilters({
                departments:
                  filters?.departments?.filter((item) => item !== department) ||
                  [],
              })
            }
          >
            <span className="far fa-times" aria-hidden="true"></span>
          </button>
        </div>
      ))}
    </div>
  )
  const jobTitleFilterBody = (
    <>
      <DropdownMenuItem className="dropdown-search dropdown-search--alternative">
        <DropdownSearchInput
          placeholder="Search filter"
          value={jobTitleFilterInput}
          onChange={(e) => setJobTitleFilterInput(e.target.value)}
        />
      </DropdownMenuItem>

      {jobTitleFilterInput && (
        <>
          <li>
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              className="dropdown-search__create"
              onClick={handleCreateCustomJobTitle}
              data-controller="tooltip"
              data-bs-placement="right"
              data-bs-original-title={`Search for "${jobTitleFilterInput}" in job titles`}
            >
              <span className="fw-medium">Search for</span>
              <span>&quot;{jobTitleFilterInput}&quot;</span>
              <span className="text-muted text-2xs ml-auto">
                {filteredJobTitles.length}
              </span>
            </Button>
          </li>
          <li className="dropdown-search-metas">
            <span className="dropdown-search-metas__count">
              {filteredJobTitles.length} results
            </span>
            <div className="dropdown-search-metas__actions ml-auto">
              <Button
                variant="link"
                size="xs"
                onClick={handleSelectAllJobTitles}
              >
                Select all
              </Button>
              <Button variant="link" size="xs" onClick={handleResetJobTitles}>
                Reset
              </Button>
            </div>
          </li>
        </>
      )}

      <div
        ref={jobTitleListRef}
        style={{ maxHeight: "300px", overflow: "auto" }}
      >
        <div
          style={{
            height: `${jobTitleVirtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {jobTitleVirtualizer.getVirtualItems().map((virtualItem) => {
            const title = uniqueTitles[virtualItem.index]
            if (!title) return null
            let count = 0
            if (customJobTitles.includes(title)) {
              count = filteredJobTitles.filter((item) =>
                item.toLowerCase().includes(title.toLowerCase()),
              ).length
            } else {
              count = filteredJobTitles.filter((item) => item === title).length
            }

            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={jobTitleVirtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <DropdownMenuItem>
                  <Checkbox
                    checked={selectedJobTitles.includes(title)}
                    id={`filter-job-title-${virtualItem.index}`}
                    name="filter-job-title"
                    onChange={() =>
                      setSelectedJobTitles((prev) =>
                        prev.includes(title)
                          ? prev.filter((item) => item !== title)
                          : [...prev, title],
                      )
                    }
                  />
                  <label
                    htmlFor={`filter-job-title-${virtualItem.index}`}
                    className="d-flex justify-content-between text-wrap"
                  >
                    <span>{highlightMatch(title, jobTitleFilterInput)}</span>
                    <span className="text-muted text-2xs">{count}</span>
                  </label>
                </DropdownMenuItem>
              </div>
            )
          })}
        </div>
      </div>

      <li>
        <DropdownMenuSubmit onClick={() => applyFilters()}>
          {i18n.t("js.multi_domain_search.filters.apply_filters")}
        </DropdownMenuSubmit>
      </li>
    </>
  )
  const jobTitleFilter = (
    <div className="h-filter" key="job_title">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            ref={jobTitleTriggerRef}
          >
            {i18n.t("js.multi_domain_search.filters.job_title")}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="overflow-visible">
          {jobTitleFilterBody}
        </DropdownMenuContent>
      </DropdownMenu>
      {filters?.jobTitles?.length > 0 && (
        <div className="h-filter__value">
          {i18n.t("js.multi_domain_search.filters.job_titles", {
            count: filters?.jobTitles?.length,
          })}
          <button
            className="btn-empty h-filter__remove"
            onClick={() => applyFilters({ jobTitles: [] })}
          >
            <span className="far fa-times" aria-hidden="true"></span>
          </button>
        </div>
      )}
    </div>
  )
  const requiredFieldFilterBody = (
    <>
      {Object.entries(requiredFieldsList).map(([key, value]) => (
        <DropdownMenuItem key={key}>
          <input
            type="checkbox"
            name="filter-required-field"
            value={key}
            className="h-checkbox"
            id={`filter-required-field-${key}`}
            checked={selectedRequiredFields.includes(key as RequiredField)}
            onChange={() => {
              setSelectedRequiredFields((prev) =>
                prev.includes(key as RequiredField)
                  ? prev.filter((item) => item !== (key as RequiredField))
                  : [...prev, key as RequiredField],
              )
            }}
          />
          <label htmlFor={`filter-required-field-${key}`}>{value}</label>
        </DropdownMenuItem>
      ))}
      <DropdownMenuSubmit onClick={() => applyFilters()}>
        {i18n.t("js.multi_domain_search.filters.apply_filters")}
      </DropdownMenuSubmit>
    </>
  )
  const requiredFieldFilter = (
    <div className="h-filter" key="required_field">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            ref={requiredFieldTriggerRef}
          >
            {i18n.t("js.multi_domain_search.filters.required_field")}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>{requiredFieldFilterBody}</DropdownMenuContent>
      </DropdownMenu>
      {filters?.requiredFields?.map((field) => (
        <div className="h-filter__value" key={field}>
          {requiredFieldsList[field]}
          <button
            className="btn-empty h-filter__remove"
            onClick={() =>
              applyFilters({
                requiredFields:
                  filters?.requiredFields?.filter((item) => item !== field) ||
                  [],
              })
            }
          >
            <span className="far fa-times" aria-hidden="true"></span>
          </button>
        </div>
      ))}
    </div>
  )
  const adjustLocationFilter = (locations: SerializedLocation[]) => {
    const newLocation = locations.map((location) => {
      return {
        value: location.value,
        type: location.type,
        label: location.label,
        included: true,
      }
    })
    setSelectedLocation(newLocation)
  }
  const loadLocations = (
    inputValue: string,
    callback: (options: Options<Location>) => void,
  ) => {
    fetch(
      locations_path({
        query: inputValue,
      }),
    )
      .then((response) => response.json())
      .then((data) => [...new Set(data)])
      .then((data) => {
        return data
          .filter(
            (x, index, col) =>
              col.findIndex((e) => e.value == x.value) === index,
          )
          .reduce((acc: GroupedOption[], item) => {
            if (!acc.find((group) => group.label === item.type)) {
              acc.push({ label: item.type, options: [] })
            }
            acc.find((group) => group.label === item.type).options.push(item)
            return acc
          }, [])
      })
      .then((data: GroupedOption[]) => callback(data))
      .catch((error) => console.error(error))
  }
  const changeLocationInclusion = (location: Location, included: boolean) => {
    const newLocation = selectedLocation.map((loc) => {
      if (loc.label === location.label) {
        return { ...loc, included }
      }
      return loc
    })
    setSelectedLocation(newLocation)
  }
  const locationTags = selectedLocation?.map((location) => {
    const { label, value, included } = location

    return (
      <div className="tags-list flex-nowrap mt-2" key={label}>
        <Tag variant={included ? "" : "danger"}>
          {label}
          <span
            className="far fa-times ml-1"
            onClick={() =>
              setSelectedLocation((prev) =>
                prev.filter((loc) => loc.value !== value),
              )
            }
          ></span>
        </Tag>
        <div className="tags-list__actions">
          <button
            className={`tags-list__action ${included ? "active" : ""}`}
            onClick={() => changeLocationInclusion(location, true)}
          >
            {i18n.t("js.multi_domain_search.filters.location.include")}
          </button>
          <button
            className={`tags-list__action ${included ? "" : "active"}`}
            onClick={() => changeLocationInclusion(location, false)}
          >
            {i18n.t("js.multi_domain_search.filters.location.exclude")}
          </button>
        </div>
      </div>
    )
  })
  type GroupedOption = {
    label: string
    options: Options<Location>
  }
  const formatGroupLabel = (data: GroupedOption) => (
    <div className="optgroup-heading">{data.label}</div>
  )
  const locationFilterBody = (
    <>
      <AsyncSelect<Location, false, GroupedOption>
        placeholder="Search for locations..."
        isMulti={true}
        value={selectedLocation?.map((location) => ({
          value: location.value,
          label: location.label,
        }))}
        loadOptions={loadLocations}
        formatGroupLabel={formatGroupLabel}
        onChange={adjustLocationFilter}
      />

      {locationTags}
      <DropdownMenuSubmit onClick={() => applyFilters()}>
        Apply filters
      </DropdownMenuSubmit>
    </>
  )
  const locationFilter = (
    <div className="h-filter" key="location">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            ref={locationTriggerRef}
          >
            {i18n.t("js.multi_domain_search.filters.location.title")}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="overflow-visible location-filter-dropdown">
          {locationFilterBody}
        </DropdownMenuContent>
      </DropdownMenu>
      {filters?.location?.length > 0 && (
        <div className="h-filter__value">
          {i18n.t("js.multi_domain_search.filters.location.number", {
            count: filters?.location?.length,
          })}
          <button
            className="btn-empty h-filter__remove"
            onClick={() => applyFilters({ location: [] })}
          >
            <span className="far fa-times" aria-hidden="true"></span>
          </button>
        </div>
      )}
    </div>
  )
  const peoplePerCompanyFilterBody = (
    <>
      <DropdownMenuItem key="people_per_company_all">
        <Radio
          name="people-per-company"
          value="all"
          id="people-per-company-all"
          checked={selectedPeoplePerCompany.type === "all"}
          onChange={() => setSelectedPeoplePerCompany({ type: "all" })}
        />
        <label htmlFor="people-per-company-all">
          {i18n.t("js.multi_domain_search.filters.people_per_company.all")}
        </label>
      </DropdownMenuItem>
      <DropdownMenuItem key="people_per_company_decision_makers">
        <Radio
          name="people-per-company"
          value="decision_makers"
          id="people-per-company-decision-makers"
          checked={selectedPeoplePerCompany.type === "decision_makers"}
          onChange={() =>
            setSelectedPeoplePerCompany({ type: "decision_makers" })
          }
        />
        <label htmlFor="people-per-company-decision-makers">
          {i18n.t(
            "js.multi_domain_search.filters.people_per_company.decision_makers",
          )}
        </label>
      </DropdownMenuItem>
      <DropdownMenuItem key="people_per_company_specific">
        <Radio
          name="people-per-company"
          value="specific_number"
          id="people-per-company-specific"
          checked={selectedPeoplePerCompany.type === "specific_number"}
          onChange={() =>
            setSelectedPeoplePerCompany({
              type: "specific_number",
              specificNumber: selectedPeoplePerCompany.specificNumber || 5,
            })
          }
        />
        <label htmlFor="people-per-company-specific">
          {i18n.t(
            "js.multi_domain_search.filters.people_per_company.specific_number",
          )}
        </label>
        {selectedPeoplePerCompany.type === "specific_number" && (
          <DropdownMenuInput>
            <div className="h-input-group">
              <input
                className="h-input h-input--sm"
                type="number"
                min="1"
                max="100"
                style={{ width: "100px" }}
                value={selectedPeoplePerCompany.specificNumber}
                onChange={(e) =>
                  setSelectedPeoplePerCompany({
                    type: "specific_number",
                    specificNumber: parseInt(e.target.value, 10),
                  })
                }
              />
            </div>
          </DropdownMenuInput>
        )}
      </DropdownMenuItem>

      <DropdownMenuSubmit onClick={() => applyFilters()}>
        {i18n.t("js.multi_domain_search.filters.apply_filters")}
      </DropdownMenuSubmit>
    </>
  )
  const peoplePerCompanyFilter = (
    <div className="h-filter" key="people_per_company">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            ref={peoplePerCompanyTriggerRef}
          >
            {i18n.t("js.multi_domain_search.filters.people_per_company.title")}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>{peoplePerCompanyFilterBody}</DropdownMenuContent>
      </DropdownMenu>
      {filters?.peoplePerCompany && (
        <div className="h-filter__value">
          {filters.peoplePerCompany.type === "decision_makers"
            ? i18n.t(
                "js.multi_domain_search.filters.people_per_company.decision_makers",
              )
            : filters.peoplePerCompany.type === "specific_number"
              ? i18n.t(
                  "js.multi_domain_search.filters.people_per_company.people",
                  {
                    count: filters.peoplePerCompany.specificNumber,
                  },
                )
              : "All"}
          <button
            className="btn-empty h-filter__remove"
            onClick={() => applyFilters({ peoplePerCompany: undefined })}
          >
            <span className="far fa-times" aria-hidden="true"></span>
          </button>
        </div>
      )}
    </div>
  )
  const similarityScoreFilterBody = (
    <>
      <div className="px-3 py-2">
        <div className="d-flex flex-column gap-2">
          <label htmlFor="similarity-min" className="form-label small mb-0">
            {i18n.t("js.lead_lookalikes.filters.higher_than")}
          </label>
          <input
            type="number"
            id="similarity-min"
            className="form-control form-control-sm"
            min="0"
            max="100"
            value={selectedSimilarityRange[0]}
            onChange={(e) => {
              const value = Math.max(
                0,
                Math.min(100, parseInt(e.target.value) || 0),
              )
              setSelectedSimilarityRange([value, 100])
            }}
          />
        </div>
      </div>
      <DropdownMenuSubmit onClick={() => applyFilters()}>
        {i18n.t("js.multi_domain_search.filters.apply_filters")}
      </DropdownMenuSubmit>
    </>
  )
  const similarityScoreFilter = (
    <div className="h-filter" key="similarity_score">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-filter__button"
            size="sm"
            ref={similarityScoreTriggerRef}
          >
            {i18n.t("js.lead_lookalikes.filters.similarity_score")}
            <span className="far fa-angle-down" aria-hidden="true"></span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>{similarityScoreFilterBody}</DropdownMenuContent>
      </DropdownMenu>
      {filters?.similarityRange && filters?.similarityRange[0] !== 40 && (
        <div className="h-filter__value">
          {i18n.t("js.lead_lookalikes.filters.higher_than")}{" "}
          {filters?.similarityRange[0]}%
          <button
            className="btn-empty h-filter__remove"
            onClick={() => applyFilters({ similarityRange: undefined })}
          >
            <span className="far fa-times" aria-hidden="true"></span>
          </button>
        </div>
      )}
    </div>
  )
  const filtersMap: Record<VisibleFilters, React.ReactNode> = {
    department: departmentFilter,
    job_title: jobTitleFilter,
    already_saved: alreadySavedFilter,
    verification_status: verificationStatusFilter,
    required_field: requiredFieldFilter,
    people_per_company: peoplePerCompanyFilter,
    location: locationFilter,
    similarity_score: similarityScoreFilter,
  }
  const filterBodiesMap: Record<VisibleFilters, React.ReactNode> = {
    department: departmentFilterBody,
    job_title: jobTitleFilterBody,
    already_saved: alreadySavedFilterBody,
    verification_status: verificationStatusFilterBody,
    required_field: requiredFieldFilterBody,
    people_per_company: peoplePerCompanyFilterBody,
    location: locationFilterBody,
    similarity_score: similarityScoreFilterBody,
  }
  return (
    <div className="ds-filters flex-nowrap" id={props.id}>
      <div className="d-flex flex-wrap gap-2">
        {additionalFilter}
        {visibleFilters.map((filter) => filtersMap[filter])}
        {nonVisibleFilters.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild data-bs-auto-close="outside">
              <Button variant="ghost" size="sm" ref={addFilterTriggerRef}>
                {i18n.t("js.multi_domain_search.filters.add_filter")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className={`overflow-visible ${
                currentFilter === "location" && "location-filter-dropdown show"
              }`}
            >
              {currentFilter && (
                <>
                  <DropdownMenuHeader>
                    <Button variant="ghost" iconOnly size="xs">
                      <span
                        className="far fa-arrow-left"
                        aria-hidden="true"
                        onClick={() => setCurrentFilter(null)}
                      ></span>
                    </Button>
                    <DropdownMenuTitle>
                      {i18n.t(allFilters[currentFilter as VisibleFilters])}
                    </DropdownMenuTitle>
                  </DropdownMenuHeader>
                  {filterBodiesMap[currentFilter as VisibleFilters]}
                </>
              )}

              {!currentFilter &&
                nonVisibleFilters.map((filter) => (
                  <li key={filter}>
                    <DropdownMenuItem
                      key={filter}
                      onClick={() => setCurrentFilter(filter)}
                      asChild
                    >
                      <button>
                        {i18n.t(allFilters[filter as VisibleFilters])}
                      </button>
                    </DropdownMenuItem>
                  </li>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {anyFiltersSet && (
        <Button
          variant="ghost"
          size="sm"
          className="ds-filters__clear"
          onClick={clearFilters}
        >
          {i18n.t("js.multi_domain_search.filters.clear_filters")}
        </Button>
      )}
    </div>
  )
}

export {
  Filters,
  departmentsList,
  typesList,
  requiredFieldsList,
  sortFunction,
  filterFunction,
  clearFilters,
}
export type {
  FiltersType,
  FiltersProps,
  Department,
  RequiredField,
  PeoplePerCompany,
  Type,
  LocationsMappingsType,
}
